<template>
  <div>
    <grid
      ref="grid"
      :list-url="listUrl"
      :new-header-action-emit="true"
      :add-checkmarks="withCheckmarks"
      :show-total-records="true"
      :reset-filter="true"
      :header-class-fn="gridHeaderClassFn"
      @item-action="onGridItemAction"
      @header-action="onGridHeaderAction"
      @meta-loaded="onGridMetaLoaded"
      @links-loaded="onGridLinksLoaded"
    />
    <teleport
      to="#vue-portal-target"
      append
    >
      <modal
        ref="editModal"
        :form-url="editFormUrl"
        modal-title="Edit details"
        modal-dialog-class="modal-lg"
        @remote-form-loaded="onEditFormLoaded"
        @success="onEditFormSuccess"
      />
      <modal-alert
        ref="modalAlert"
      />
      <approve-candidates-modal
        ref="approveCandidatesModal"
        @success="onCandidatesApprovedSuccess"
      />
      <modal-confirmation
        ref="actionConfirmationModal"
        @confirmed="onActionConfirmed"
      >
        <template #confirmation-content>
          <div class="row gx-2">
            <div class="mb-3 col-12">
              <label class="form-label" for="candidate">Candidate</label>
              <input
                v-model="selectedCandidateName"
                type="text"
                class="form-control form-control-sm"
                readonly
              >
            </div>
          </div>
        </template>
      </modal-confirmation>
      <modal-confirmation
        ref="cancelConfirmationModal"
        @confirmed="onActionConfirmed"
      >
        <template #confirmation-content="{ confirmationResult }">
          <div class="row gx-2">
            <div class="mb-3 col-12">
              <label class="form-label" for="candidate">Candidate</label>
              <input
                v-model="selectedCandidateName"
                type="text"
                class="form-control form-control-sm"
                readonly
              >
            </div>
          </div>
          <div
            v-if="showCancelationReason(confirmationResult)"
            class="row gx-2"
          >
            <div class="mb-3 col-12">
              <label
                for="candidateCancelationReason"
                class="form-label required"
              >
                Cancelation reason
              </label>
              <textarea
                id="candidateCancelationReason"
                v-model="cancelationReason"
                class="form-control form-control-sm"
                rows="3"
              />
            </div>
          </div>
        </template>
      </modal-confirmation>
      <select-short-term-destination-modal
        ref="shortTermDestinationModal"
        @success="onPreselectedCandidateApprovedSuccess"
      />
      <select-study-visit-destination-modal
        ref="studyVisitDestinationModal"
        @success="onPreselectedCandidateApprovedSuccess"
      />
      <select-long-term-destination-modal
        ref="longTermDestinationModal"
        @success="onPreselectedCandidateApprovedSuccess"
      />
      <send-invitation-remote-modal
        ref="sendInvitationModal"
        :modal-title="sendInvitationModalTitle"
        :send-action-title="sendInvitationModalActionTitle"
        @invitation-sent="onInvitationSent"
      />
      <remote-modal
        ref="viewDetailsModal"
        modal-title="Participant details"
        modal-class="ospe-activity-participant-details"
        @details-loaded="onViewDetailsLoaded"
      >
        <template #modal-content="{ modalState }">
          <div
            v-if="!modalState.isLoading"
            v-html="participantDetailsHtml"
          />
        </template>
        <template #modal-footer>
          <a
            v-if="participantLogUrl"
            :href="participantLogUrl"
            class="btn btn-sm btn-info me-auto"
          >
            View log
          </a>
          <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </template>
      </remote-modal>
      <remote-modal
        ref="projectAllocationDetailsModal"
        :modal-form="true"
        modal-title="Allocation details"
        @form-success="onProjectAllocationDetailsSuccess"
      />
      <xlsx-upload-modal
        ref="candidatesUploadModal"
        modal-title="Upload participants"
        :upload-url="candidatesXlsxUploadUrl"
        :template-url="candidatesXlsxTemplateUrl"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {nextTick, ref, useTemplateRef} from 'vue'
import $ from 'jquery'
import type {QueryOptions} from 'select2'
import {initializeAutocompleteField, simulateFormPost} from "@/js/site-utils"
import type {ButtonAction, GridHeaderAction, GridID, GridItem, ItemAction} from '../types'
import {initializeDatePicker, isNonEmptyString, tsInitVue} from '@/js/js-utils'

import {type Confirmation, type ConfirmationResult, OspeActivityType} from '@/js/ospe-types'

import ApproveCandidatesModal from './approve-candidates-modal.vue'
import ModalAlert from '../../vue-components/modal-alert.vue'
import ModalConfirmation from '../../vue-components/modal-confirmation.vue'

import Grid from '../grid.vue'
import Modal from '../modal.vue'

import OspeShortTermChoiceSelection from './ospe-short-term-choice-selection.vue'
import OspeLongTermChoiceSelection from './ospe-long-term-choice-selection.vue'
import OspeStudyVisitChoiceSelection from './ospe-study-visit-choice-selection.vue'
import OspeExchangeApplicationFiles from './ospe-exchange-application-files.vue'
import OspeApplicationCancellationReasonControl from './application-cancellation-reason-control.vue'
import SelectShortTermDestinationModal from './select-short-term-destination-modal.vue'
import SelectStudyVisitDestinationModal from './select-study-visit-destination-modal.vue'
import SelectLongTermDestinationModal from "./select-long-term-destination-modal.vue"
import SendInvitationRemoteModal from "../send-invitation-remote-modal.vue"
import RemoteModal from "../remote-modal.vue"
import XlsxUploadModal from "../xlsx-upload-modal.vue"

defineProps({
  listUrl: { default: null, type: String },
  withCheckmarks: { default: true, type: Boolean },
})

const emit = defineEmits<{
  'data-changed': []
}>()

const grid = useTemplateRef<InstanceType<typeof Grid>>('grid')
const editModal = useTemplateRef<InstanceType<typeof Modal>>('editModal')
const modalAlert = useTemplateRef<InstanceType<typeof ModalAlert>>('modalAlert')
const approveCandidatesModal = useTemplateRef<InstanceType<typeof ApproveCandidatesModal>>('approveCandidatesModal')
const actionConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmation>>('actionConfirmationModal')
const cancelConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmation>>('cancelConfirmationModal')
const shortTermDestinationModal = useTemplateRef<InstanceType<typeof SelectShortTermDestinationModal>>('shortTermDestinationModal')
const studyVisitDestinationModal = useTemplateRef<InstanceType<typeof SelectStudyVisitDestinationModal>>('studyVisitDestinationModal')
const longTermDestinationModal = useTemplateRef<InstanceType<typeof SelectLongTermDestinationModal>>('longTermDestinationModal')
const sendInvitationModal = useTemplateRef<InstanceType<typeof SendInvitationRemoteModal>>('sendInvitationModal')
const viewDetailsModal = useTemplateRef<InstanceType<typeof RemoteModal>>('viewDetailsModal')
const projectAllocationDetailsModal = useTemplateRef<InstanceType<typeof RemoteModal>>('projectAllocationDetailsModal')
const candidatesUploadModal = useTemplateRef<InstanceType<typeof XlsxUploadModal>>('candidatesUploadModal')

const editFormUrl = ref('')
const shortTermChoiceSelection = ref<InstanceType<typeof OspeShortTermChoiceSelection> | null>(null)
const longTermChoiceSelection = ref<InstanceType<typeof OspeLongTermChoiceSelection> | null>(null)
const studyVisitChoiceSelection = ref<InstanceType<typeof OspeStudyVisitChoiceSelection> | null>(null)
const filesControl = ref<InstanceType<typeof OspeExchangeApplicationFiles> | null>(null)
const selectedCandidateName = ref('')
const cancelationReason = ref<string | null>(null)
const activityType = ref<OspeActivityType | null>(null)
const sendInvitationModalTitle = ref<string | undefined>()
const sendInvitationModalActionTitle = ref('Send invitation')
const candidatesXlsxUploadUrl = ref<string | undefined>()
const candidatesXlsxTemplateUrl = ref<string | undefined>()
const participantDetailsHtml = ref('')
const participantLogUrl = ref<string | null>(null)
const editCancelationReasonCOntrol = ref<any>(null)

const onGridMetaLoaded = (meta: any): void => {
  if (meta.activityTypeId) {
    activityType.value = meta.activityTypeId
  }
}

const onGridLinksLoaded = (links: Record<string, string> | null): void => {
  if (links) {
    if (isNonEmptyString(links.uploadTemplate)) {
      candidatesXlsxTemplateUrl.value = links.uploadTemplate
    }
  }
}

const onGridItemAction = (item: GridItem, action: ItemAction | string): void => {
  let isEditAction = false
  if (typeof action === 'string') {
    isEditAction = action === 'edit'
  } else {
    isEditAction = action.name === 'edit'
  }

  if (isEditAction) {
    if (item && item.links && isNonEmptyString(item.links.edit)) {
      editFormUrl.value = item.links.edit
      nextTick(() => {
        editModal.value!.showModal()
      })
    }
  } else if (typeof action !== 'string') {
    if (isNonEmptyString(action.name) && isNonEmptyString(action.url)) {
      if (action.name === 'select') {
        approveCandidatesModal.value!.show([item.id], action.url, item.attributes.full_name)
      } else if (action.name === 'cancel-application') {
        showCancelCandidatePrompt([item.id], action.url)
      } else if (action.name === 'approve-canceled-application') {
        showApproveCanceledCandidatePrompt([item.id], action.url)
      } else if (action.name == 'unselect-preselected') {
        showUnselectPreselectedCandidatePrompt([item.id], action.url)
      } else if (action.name == 'select-preselected') {
        showSelectPreselectedCandidatePrompt([item.id], action.url)
      } else if (action.name == 'unselect-approved') {
        showUnselectApprovedCandidatePrompt([item.id], action.url)
      } else if (action.name == 'edit-destination') {
        showEditParticipantDestinationPrompt([item.id], action.url)
      } else if (action.name == 'edit-destination-single') {
        showEditParticipantDestinationPrompt(null, action.url)
      } else if (action.name == 'resend-application-received') {
        showResendApplicationConfirmationEmail([item.id], action.url)
      } else if (action.name == 'send-invitation' || action.name == 're-send-invitation') {
        showSendInvitationModal([item.id], action.url, action.title)
      } else if (action.name == 're-send-registration-confirmation') {
        showSendRegistrationConfirmationModal([item.id], action.url)
      } else if (action.name == 'view-details') {
        showParticipantDetails(action.url)
      } else if (action.name == 'project-allocation-details') {
        showParticipantAllocationDetails(action.url)
      }
    }
  }
}

const getSelectedCandidatesName = (selectedItemIds: GridID[]): string => {
  let candidateName = `${selectedItemIds.length} candidates`
  if (selectedItemIds.length == 1) {
    const candidates = grid.value!.getItems(selectedItemIds)
    if (candidates.length == 1) {
      candidateName = candidates[0].attributes.full_name
    }
  }
  return candidateName
}

const onGridHeaderAction = (actionOrName: string | GridHeaderAction | ButtonAction): void => {
  if (typeof actionOrName === 'string') {
    return
  }

  const action = actionOrName
  if (action.name === 'new' && isNonEmptyString(action.url)) {
    editFormUrl.value = action.url
    nextTick(() => {
      editModal.value!.showModal()
    })
  } else if (action.name === 'approve-candidates') {
    const selectedItemIds = grid.value!.selectedItemIds
    if (selectedItemIds.length == 0) {
      modalAlert.value!.show('Select candidates', 'Please choose at least one candidate', 'info')
      return
    }
    approveCandidatesModal.value!.show(selectedItemIds, action.url, getSelectedCandidatesName(selectedItemIds))
  } else if (action.name === 'cancel-candidates') {
    const selectedItemIds = grid.value!.selectedItemIds
    showCancelCandidatePrompt(selectedItemIds, action.url)
  } else if (action.name === 'approve-canceled-candidates') {
    const selectedItemIds = grid.value!.selectedItemIds
    showApproveCanceledCandidatePrompt(selectedItemIds, action.url)
  } else if (action.name == 'unselect-preselected') {
    const selectedItemIds = grid.value!.selectedItemIds
    showUnselectPreselectedCandidatePrompt(selectedItemIds, action.url)
  } else if (action.name == 'select-preselected') {
    const selectedItemIds = grid.value!.selectedItemIds
    showSelectPreselectedCandidatePrompt(selectedItemIds, action.url)
  } else if (action.name == 'unselect-approved') {
    const selectedItemIds = grid.value!.selectedItemIds
    showUnselectApprovedCandidatePrompt(selectedItemIds, action.url)
  } else if (action.name == 'send-invitations' || action.name == 're-send-invitations') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    showSendInvitationModal(selectedParticipantIds, action.url, action.title)
  } else if (action.name == 'move-to-pre-payment') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    showMoveToPrePaymentPrompt(selectedParticipantIds, action.url)
  } else if (action.name == 'move-to-final-payment') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    showMoveToFinalPaymentPrompt(selectedParticipantIds, action.url)
  } else if (action.name == 'remove-payment') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    showRemovePaymentPrompt(selectedParticipantIds, action.url)
  } else if (action.name == 'download-registration-details') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    if (selectedParticipantIds.length == 0) {
      modalAlert.value!.show('Select participant', 'Please choose at least one participant', 'info')
      return
    }

    downloadRegistrationDetailsPdf(action.url, selectedParticipantIds)
  } else if (action.name == 'download-application-details') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    if (selectedParticipantIds.length == 0) {
      modalAlert.value!.show('Select participant', 'Please choose at least one participant', 'info')
      return
    }

    if (grid.value!.checkAll) {
      downloadApplicationDetailsPdf(action.url, 'all')
    } else {
      downloadApplicationDetailsPdf(action.url, selectedParticipantIds)
    }
  } else if (action.name == 'upload') {
    candidatesXlsxUploadUrl.value = action.url
    candidatesUploadModal.value!.showModal(null)
  } else if (action.name == 'edit-destination') {
    const selectedParticipantIds = grid.value!.selectedItemIds
    if (selectedParticipantIds.length == 0) {
      modalAlert.value!.show('Select participant', 'Please choose at least one participant', 'info')
      return
    }
    showEditParticipantDestinationPrompt(selectedParticipantIds, action.url)
  } else if (action.name == 'download-application-documents' || action.name?.startsWith('download-files:')) {
    downloadApplicationDocuments(action.title, action.url)
  }
}

const onEditFormLoaded = (): void => {
  const modalRootEl = editModal.value!.$el

  // initialize date picker
  const datePickers = modalRootEl.querySelectorAll(':scope div.input-group.date')
  for (let i = 0; i < datePickers.length; i++) {
    const dpEl = datePickers[i]
    if (dpEl instanceof HTMLElement) {
      initializeDatePicker(dpEl)
    }
  }

  // land management
  const ncpSelectEl = modalRootEl.querySelector('select[ref="ospe-ncp"]')
  let ncpAutocompleteUrl = ''
  if (ncpSelectEl instanceof HTMLSelectElement) {
    ncpAutocompleteUrl = ncpSelectEl.dataset.autocompleteUrl || ''
    initializeAutocompleteField(ncpSelectEl, undefined, undefined, function (options) {
      delete options.minimumInputLength // this makes list to be pre-loaded when dropdown is opened

      if (options?.ajax && typeof options.ajax?.url === 'function') {
        ncpAutocompleteUrl = options.ajax.url({})
        options.ajax.url = function (_params: QueryOptions): string {
          return ncpAutocompleteUrl || ''
        }
      }

      options['templateSelection'] = function (data: any, _container: any): string {
        if (data.requireId == '1') {
          data.element.dataset.requireId = '1'
        }
        return data.text;
      }
      return options
    })
  }

  const nationalitySelectEl = modalRootEl.querySelector(':scope select[ref="nationality"]')
  if (nationalitySelectEl instanceof HTMLSelectElement && ncpSelectEl instanceof HTMLSelectElement) {
    const enableDisableFields = function (nationalityEl: HTMLSelectElement, landEl: Element | null): void {
      let hideLand = true

      if (nationalityEl.selectedIndex >= 0) {
        const item = nationalityEl.item(nationalityEl.selectedIndex)
        if (item instanceof HTMLOptionElement) {
          if (item.dataset.requireLand == '1') {
            hideLand = false
          }
          ncpAutocompleteUrl = item.dataset.ncpUrl || ''
        }
      }

      if (landEl instanceof HTMLElement) {
        if (hideLand) {
          landEl.classList.add('d-none')
        } else {
          landEl.classList.remove('d-none')
        }
      }
    }

    const enableDisableIdNumberField = (ncpEl: HTMLSelectElement, idNumberEl: Element | null) => {
      let hideIdNumber = true
      const selectedItems = $(ncpEl).select2('data')
      if (selectedItems.length > 0) {
        hideIdNumber = selectedItems[0].element.dataset.requireId != '1'
      }
      if (idNumberEl instanceof HTMLElement) {
        if (hideIdNumber) {
          idNumberEl.classList.add('d-none')
        } else {
          idNumberEl.classList.remove('d-none')
        }
      }
    }

    const landEl = modalRootEl.querySelector(':scope div[ref="land"]')
    const idNumberEl = modalRootEl.querySelector(':scope div[ref="id_number"]')
    nationalitySelectEl.addEventListener('change', function() {
      enableDisableFields(nationalitySelectEl, landEl)
    })
    $(ncpSelectEl).on('select2:select', () => {
      enableDisableIdNumberField(ncpSelectEl, idNumberEl)
    })
    enableDisableFields(nationalitySelectEl, landEl)
    enableDisableIdNumberField(ncpSelectEl, idNumberEl)
  }

  const categorySelectEl = modalRootEl.querySelector('select[ref="category"]')
  const specialisationWrapEl = modalRootEl.querySelector('[ref="specialisation-wrap"]')
  if (categorySelectEl instanceof HTMLSelectElement && specialisationWrapEl instanceof HTMLElement) {
    const toggleSpecialisation = function () {
      const opt = categorySelectEl.selectedOptions.item(0)
      if (opt) {
        specialisationWrapEl.classList.toggle("d-none", opt.dataset.specialised != "true")
      }
    }

    categorySelectEl.addEventListener("change", toggleSpecialisation)
    toggleSpecialisation()
  }

  const hostNcpSelectEl = modalRootEl.querySelector('select[ref="host-ospe-ncp"]')
  let hostNcpAutocompleteUrl = ''
  if (hostNcpSelectEl instanceof HTMLSelectElement) {
    hostNcpAutocompleteUrl = hostNcpSelectEl.dataset.autocompleteUrl || ''
    initializeAutocompleteField(hostNcpSelectEl, undefined, undefined, function (options) {
      delete options.minimumInputLength // this makes list to be pre-loaded when dropdown is opened

      if (options?.ajax && typeof options.ajax?.url === 'function') {
        hostNcpAutocompleteUrl = options.ajax.url({})
        options.ajax.url = function (_params: QueryOptions): string {
          return hostNcpAutocompleteUrl || ''
        }
      }

      options['templateSelection'] = function (data: any, _container: any): string {
        if (data.requireId == '1') {
          data.element.dataset.requireId = '1'
        }
        return data.text;
      }
      return options
    })
  }

  const hostCountrySelectEl = modalRootEl.querySelector(':scope select[ref="host-country"]')
  if (hostCountrySelectEl instanceof HTMLSelectElement && hostNcpSelectEl instanceof HTMLSelectElement) {
    const changeHostNcpAutocomplete = function (nationalityEl: HTMLSelectElement): void {
      if (nationalityEl.selectedIndex >= 0) {
        const item = nationalityEl.item(nationalityEl.selectedIndex)
        if (item instanceof HTMLOptionElement) {
          hostNcpAutocompleteUrl = item.dataset.ncpUrl || ''
        }
      }
    }

    hostCountrySelectEl.addEventListener('change', function() {
      changeHostNcpAutocomplete(hostCountrySelectEl)
    })
    changeHostNcpAutocomplete(hostCountrySelectEl)
  }

  const shortTermSelectionEl = modalRootEl.querySelector(':scope div[data-is="OspeShortTermChoiceSelection"]')
  if (shortTermSelectionEl instanceof HTMLElement) {

    const props = {
      availableChoicesUrl: '',
    }
    if (isNonEmptyString(shortTermSelectionEl.dataset.availableChoicesUrl)) {
      props.availableChoicesUrl = shortTermSelectionEl.dataset.availableChoicesUrl
    }
    shortTermChoiceSelection.value = tsInitVue(OspeShortTermChoiceSelection, shortTermSelectionEl, props)
  }

  const longTermSelectionEl = modalRootEl.querySelector(':scope div[data-is="OspeLongTermChoiceSelection"]')
  if (longTermSelectionEl instanceof HTMLElement) {
    const props = {
      availableChoicesUrl: '',
    }
    if (isNonEmptyString(longTermSelectionEl.dataset.availableChoicesUrl)) {
      props.availableChoicesUrl = longTermSelectionEl.dataset.availableChoicesUrl
    }
    longTermChoiceSelection.value = tsInitVue(OspeLongTermChoiceSelection, longTermSelectionEl, props)
  }

  const studyVisitSelectionEl = modalRootEl.querySelector(':scope div[data-is="OspeStudyVisitChoiceSelection"]')
  if (studyVisitSelectionEl instanceof HTMLElement) {
    const props = {
      availableChoicesUrl: '',
    }
    if (isNonEmptyString(studyVisitSelectionEl.dataset.availableChoicesUrl)) {
      props.availableChoicesUrl = studyVisitSelectionEl.dataset.availableChoicesUrl
    }
    studyVisitChoiceSelection.value = tsInitVue(OspeStudyVisitChoiceSelection, studyVisitSelectionEl, props)
  }

  const filesEl = modalRootEl.querySelector(':scope div[data-is="ospe-exchange-application-files"]')
  if (filesEl instanceof HTMLElement) {
    const uploadUrl = filesEl.dataset.uploadUrl
    let numberOfFiles = 0

    const numberOfFilesStr = filesEl.dataset.numberOfFiles
    if (isNonEmptyString(numberOfFilesStr)) {
      numberOfFiles = parseInt(numberOfFilesStr)
      if (isNaN(numberOfFiles)) {
        numberOfFiles = 0
      }
    }
    if (isNonEmptyString(uploadUrl)) {
      filesControl.value = tsInitVue(OspeExchangeApplicationFiles, filesEl, { uploadUrl, numberOfFiles })
    }
  }
}

const onEditFormSuccess = (): void => {
  editModal.value!.hideModal()
  grid.value!.reload()
}

const onCandidatesApprovedSuccess = (): void => {
  approveCandidatesModal.value!.hide()
  reload()
}

const showCancelCandidatePrompt = (candidateIds: GridID[], url: string): void => {
  if (candidateIds.length == 0) {
    modalAlert.value!.show('Cancel candidates', 'Please select at least one candidate', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(candidateIds)

  const multipleCandidates = candidateIds.length > 1

  cancelationReason.value = null
  const confirmation: Confirmation = {
    id: 'cancel',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: multipleCandidates ? 'Cancel applications' : 'Cancel application',
    message: multipleCandidates ? 'You are about to cancel applications for selected candidates' : 'You are about to cancel candidate`s application',
    actionTitle: multipleCandidates ? 'Cancel applications' : 'Cancel application',
    actionMessage: multipleCandidates ? 'Canceling applications' : 'Canceling application',
    actionRequest: {
      method: 'post',
      paramsFn: (): any => {
        return {
          candidate_ids: candidateIds,
          reason: cancelationReason.value
        }
      },
      url
    }
  }

  cancelConfirmationModal.value!.show(confirmation)
}

const showApproveCanceledCandidatePrompt = (candidateIds: GridID[], url: string): void => {
  if (candidateIds.length == 0) {
    if (activityType.value == OspeActivityType.project) {
      modalAlert.value!.show(
        'Move selected to participants list',
        'Please select at least one participant',
        'info'
      )
    } else {
      const promptTitle = activityType.value == OspeActivityType.ap ? 'Approve canceled candidates' : 'Move selected to candidates'
      modalAlert.value!.show(promptTitle, 'Please select at least one candidate', 'info')
    }
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(candidateIds)
  const multipleCandidates = candidateIds.length > 1

  let confirmationTitle = multipleCandidates ? 'Move applications to candidates list' : 'Move application to candidates list'
  let confirmationMessage = multipleCandidates ? 'You are about to move selected applications to candidates list' : 'You are about to move application to candidates list'

  if (activityType.value == OspeActivityType.ap) {
    confirmationTitle = multipleCandidates ? 'Approve selected applications' : 'Approve application'
    confirmationMessage = multipleCandidates ? 'You are about to move selected applications to participants list' : 'You are about to move application to participants list'
  } else if (activityType.value == OspeActivityType.project) {
    confirmationTitle = 'Move to participants list'
    confirmationMessage = multipleCandidates ? 'You are about to move selected applications to participants list' : 'You are about to move application to participants list'
  }

  const confirmation: Confirmation = {
    id: 'approve',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: confirmationTitle,
    message: confirmationMessage,
    actionTitle: multipleCandidates ? 'Move applications' : 'Move application',
    actionMessage: multipleCandidates ? 'Moving applications' : 'Moving application',
    actionRequest: {
      method: 'post',
      params: {
        candidate_ids: candidateIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showUnselectPreselectedCandidatePrompt = (candidateIds: GridID[], url: string): void => {
  if (candidateIds.length == 0) {
    modalAlert.value!.show('Move preselected to candidates', 'Please select at least one candidate', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(candidateIds)
  const multipleCandidates = candidateIds.length > 1

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: multipleCandidates ? 'Move preselected candidates to candidates list' : 'Move preselected candidate to candidates list',
    message: multipleCandidates ? 'You are about to move preselected candidates to candidates list' : 'You are about to move preselected candidate to candidates list',
    actionTitle: multipleCandidates ? 'Move candidates' : 'Move candidates',
    actionMessage: multipleCandidates ? 'Moving candidates' : 'Moving candidates',
    actionRequest: {
      method: 'post',
      params: {
        candidate_ids: candidateIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showSelectPreselectedCandidatePrompt = (candidateIds: GridID[], url: string): void => {
  if (candidateIds.length == 0) {
    modalAlert.value!.show('Move preselected to candidates', 'Please select at least one candidate', 'info')
    return
  }
  if (activityType.value == OspeActivityType.sv) {
    studyVisitDestinationModal.value!.show(url, candidateIds)
  } else if (activityType.value == OspeActivityType.lt) {
    longTermDestinationModal.value!.show(url, candidateIds)
  } else if (activityType.value == OspeActivityType.ge || activityType.value == OspeActivityType.se ||
              activityType.value == OspeActivityType.cepol || activityType.value == OspeActivityType.cp ||
              activityType.value == OspeActivityType.cs || activityType.value == OspeActivityType.et) {
    shortTermDestinationModal.value!.show(url, candidateIds)
  } else {
    alert('This activity type is not supported!')
  }
}

const onPreselectedCandidateApprovedSuccess = (): void => {
  if (activityType.value == OspeActivityType.sv) {
    studyVisitDestinationModal.value!.hide()
  } else if (activityType.value == OspeActivityType.lt) {
    longTermDestinationModal.value!.hide()
  } else {
    shortTermDestinationModal.value!.hide()
  }
  reload()
}

const showUnselectApprovedCandidatePrompt = (candidateIds: GridID[], url: string): void => {
  if (candidateIds.length == 0) {
    modalAlert.value!.show('Move participant to preselected candidates', 'Please select at least one participant', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(candidateIds)
  const multipleCandidates = candidateIds.length > 1

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: multipleCandidates ? 'Move participant to preselected candidates list' : 'Move participant to preselected candidates list',
    message: multipleCandidates ? 'You are about to move participant to preselected candidates list' : 'You are about to move participant to preselected candidates list',
    actionTitle: multipleCandidates ? 'Move participants' : 'Move participants',
    actionMessage: multipleCandidates ? 'Moving participants' : 'Moving participants',
    actionRequest: {
      method: 'post',
      params: {
        participant_ids: candidateIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showEditParticipantDestinationPrompt = (participantIds: GridID[] | null, url: string): void => {
  if (Array.isArray(participantIds) && participantIds.length == 0) {
    modalAlert.value!.show('Edit participant\'s destination', 'Please select at least one participant', 'info')
    return
  }
  if (activityType.value == OspeActivityType.sv) {
    studyVisitDestinationModal.value!.show(url, participantIds, true)
  } else if (activityType.value == OspeActivityType.lt) {
    longTermDestinationModal.value!.show(url, participantIds, true)
  } else if (activityType.value == OspeActivityType.ge || activityType.value == OspeActivityType.se ||
              activityType.value == OspeActivityType.cepol || activityType.value == OspeActivityType.cp ||
              activityType.value == OspeActivityType.cs || activityType.value == OspeActivityType.et) {
    shortTermDestinationModal.value!.show(url, participantIds, true)
  }
}

const showResendApplicationConfirmationEmail = (applicationIds: GridID[], url: string): void => {
  if (applicationIds.length == 0) {
    modalAlert.value!.show('Re-send confirmation email', 'Please select at least one application', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(applicationIds)

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: 'Re-send application confirmation e-mail',
    message: 'You are about to re-send application confirmation e-mail',
    actionTitle: 'Re-send e-mail',
    actionMessage: 'Sending e-mail',
    actionRequest: {
      method: 'post',
      params: {
        application_ids: applicationIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const onActionConfirmed = (): void => {
  reload()
  emit('data-changed')
}

const reload = (): void => {
  grid.value!.reload()
}

const showCancelationReason = (confirmationResult: ConfirmationResult | null): boolean => {
  if (confirmationResult) {
    if (confirmationResult.isSuccess) {
      return false
    }
  }
  return true
}

const showSendInvitationModal = (participantIds: GridID[], url: string, title: string): void => {
  if (participantIds.length == 0) {
    modalAlert.value!.show(title, 'Please select at least one participant', 'info')
    return
  }

  sendInvitationModalTitle.value = title
  sendInvitationModalActionTitle.value = 'Send invitation'
  sendInvitationModal.value!.showModal(url, participantIds)
}

const onInvitationSent = (): void => {
  reload()
}

const showMoveToPrePaymentPrompt = (participantIds: GridID[], url: string): void => {
  if (participantIds.length == 0) {
    modalAlert.value!.show('Move to prepayment list', 'Please select at least one participant', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(participantIds)
  const multipleParticipants = participantIds.length > 1

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: multipleParticipants ? 'Move participants to prepayment list' : 'Move participant to prepayment list',
    message: multipleParticipants ? `You are about to move ${participantIds.length} participants to prepayment list` : 'You are about to move participant to prepayment list',
    actionTitle: multipleParticipants ? 'Move participants' : 'Move participant',
    actionMessage: multipleParticipants ? 'Moving participants' : 'Moving participant',
    actionRequest: {
      method: 'post',
      params: {
        participant_ids: participantIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showMoveToFinalPaymentPrompt = (participantIds: GridID[], url: string): void => {
  if (participantIds.length == 0) {
    modalAlert.value!.show('Move to final payment list', 'Please select at least one participant', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(participantIds)
  const multipleParticipants = participantIds.length > 1

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: multipleParticipants ? 'Move participants to final payment list' : 'Move participant to final payment list',
    message: multipleParticipants ? `You are about to move ${participantIds.length} participants to final payment list` : 'You are about to move participant to final payment list',
    actionTitle: multipleParticipants ? 'Move participants' : 'Move participant',
    actionMessage: multipleParticipants ? 'Moving participants' : 'Moving participant',
    actionRequest: {
      method: 'post',
      params: {
        participant_ids: participantIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showRemovePaymentPrompt = (participantIds: GridID[], url: string): void => {
  if (participantIds.length == 0) {
    modalAlert.value!.show('Remove payment details', 'Please select at least one participant', 'info')
    return
  }

  selectedCandidateName.value = getSelectedCandidatesName(participantIds)
  const multipleParticipants = participantIds.length > 1

  const confirmation: Confirmation = {
    id: 'confirmation',
    type: 'confirmations',
    displayMode: 'alert',
    alertType: 'alert-info',
    title: 'Remove payment details',
    message: multipleParticipants ? `You are about to move ${participantIds.length} participants back to participants list` : 'You are about to move participant back to participants list',
    actionTitle: multipleParticipants ? 'Move participants' : 'Move participant',
    actionMessage: multipleParticipants ? 'Moving participants' : 'Moving participant',
    actionRequest: {
      method: 'post',
      params: {
        participant_ids: participantIds,
      },
      url
    }
  }

  actionConfirmationModal.value!.show(confirmation)
}

const showParticipantDetails = (url: string): void => {
  participantDetailsHtml.value = ''
  participantLogUrl.value = null
  viewDetailsModal.value!.showModal(url)
}

const onViewDetailsLoaded = (data: any): void => {
  participantLogUrl.value = null
  if (isNonEmptyString(data.detailsHtml)) {
    participantDetailsHtml.value = data.detailsHtml
    if (isNonEmptyString(data.logUrl)) {
      participantLogUrl.value = data.logUrl
    }
  } else if (isNonEmptyString(data)) {
    participantDetailsHtml.value = data
  }

  nextTick(() => {
    const v = viewDetailsModal.value!.$el.querySelector('div[data-is="ApplicationCancelationReasonControl"]')
    if (v instanceof HTMLElement) {
      editCancelationReasonCOntrol.value = tsInitVue(OspeApplicationCancellationReasonControl, v, {
        cancellationReason: v.dataset.cancelationReason,
        updateUrl: v.dataset.updateUrl,
      })
    }
  })
}

const showSendRegistrationConfirmationModal = (participantIds: GridID[], url: string): void => {
  if (participantIds.length == 0) {
    modalAlert.value!.show('Re-send registration confirmation', 'Please select at least one participant', 'info')
    return
  }

  sendInvitationModalTitle.value = "Re-send registration confirmation"
  sendInvitationModalActionTitle.value = 'Send confirmation'
  sendInvitationModal.value!.showModal(url, participantIds)
}

const downloadRegistrationDetailsPdf = (url: string, participantIds: 'all' | GridID[]): void => {
  const requestParams = {}
  Object.assign(requestParams, grid.value!.listQueryParams(false), {
    participant_ids: Array.isArray(participantIds) ? participantIds.join(',') : participantIds
  })
  simulateFormPost(url, requestParams)
}

const downloadApplicationDetailsPdf = (url: string, participantIds: 'all' | GridID[]): void => {
  const requestParams = {}
  Object.assign(requestParams, grid.value!.listQueryParams(false), {
    participant_ids: Array.isArray(participantIds) ? participantIds.join(',') : participantIds
  })
  simulateFormPost(url, requestParams)
}

const downloadApplicationDocuments = (actionTitle: string, url: string): void => {
  const selectedParticipantIds = grid.value!.selectedItemIds
  if (selectedParticipantIds.length == 0) {
    modalAlert.value!.show(actionTitle, 'Please choose at least one participant', 'info')
    return
  }

  const requestParams = {}
  Object.assign(requestParams, grid.value!.listQueryParams(false), {
    participant_ids: selectedParticipantIds.join(',')
  })
  simulateFormPost(url, requestParams)
}

const showParticipantAllocationDetails = (url: string): void => {
  projectAllocationDetailsModal.value!.showModal(url)
}

const onProjectAllocationDetailsSuccess = (): void => {
  reload()
  projectAllocationDetailsModal.value!.hideModal()
}

const gridHeaderClassFn = (column: any): string => {
  const hc = grid.value!.fHeaderClassBase(column)
  if (column.name === 'application_date_time_str') {
    return `tight ${hc}`
  }
  return hc
}

defineExpose({
  reload,
})
</script>
